// Client for the fresh-crm-api Cloudflare Worker (SPEC §3).
// Every call attaches the current Supabase session JWT as a Bearer token.

import { supabase } from "./supabase";
import type { Id, Message, SegmentDef } from "./types";

/** Worker base URL from env, without a trailing slash. */
export const API_URL: string = (import.meta.env.VITE_CRM_API_URL || "").replace(/\/+$/, "");

export interface BulkSmsArgs {
  segment_id?: Id;
  definition?: SegmentDef;
  client_ids?: Id[];
  body: string;
}

export interface BulkSmsSkipped {
  client_id: Id;
  reason: string;
}

/** Aggregated result of a full chunk-looped bulk send. */
export interface BulkSmsResult {
  sent: number;
  skipped: BulkSmsSkipped[];
}

/** One chunk's response from POST /sms/bulk (worker caps 40 sends per call). */
interface BulkChunkResponse {
  sent: number;
  skipped: BulkSmsSkipped[];
  remaining_client_ids: Id[];
  rate_limited?: boolean;
  retry_after_seconds?: number | null;
}

/**
 * Progress callback for sendBulk: sentSoFar messages out of totalPlanned
 * recipients (totalPlanned includes recipients later skipped, so it is stable
 * from the first chunk onward).
 */
export type BulkProgress = (sentSoFar: number, totalPlanned: number) => void;

/**
 * Thrown when a bulk send fails after at least one chunk already went out.
 * `partial` carries what was actually sent/skipped before the failure.
 */
export class BulkSendInterruptedError extends Error {
  partial: BulkSmsResult;
  cause_message: string;
  constructor(partial: BulkSmsResult, causeMessage: string) {
    super(
      `The bulk send stopped partway: ${partial.sent} message${partial.sent === 1 ? "" : "s"} ` +
        `already went out before a batch failed (${causeMessage}). ` +
        `Do not blindly re-send to everyone — the remaining clients have not been messaged yet.`,
    );
    this.name = "BulkSendInterruptedError";
    this.partial = partial;
    this.cause_message = causeMessage;
  }
}

/** iMessage availability probe result (GET /sms/imessage → Project Blue). */
export interface IMessageCheck {
  normalizedPhone: string;
  isIMessageAvailable: boolean;
}

/** Per-campaign stats block of a /campaigns/run response. */
export interface CampaignRunStats {
  campaign_id: string;
  name: string;
  due: number;
  sent: number;
  errors: number;
}

/** Response of POST /campaigns/run — mirrors worker/src/campaigns.ts. */
export interface CampaignRunResult {
  total_sent: number;
  total_errors: number;
  cap: number;
  capped: boolean;
  rate_limited?: boolean;
  retry_after_seconds?: number | null;
  campaigns: CampaignRunStats[];
}

async function authHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

function extractError(body: unknown, fallback: string): string {
  if (body && typeof body === "object") {
    const rec = body as Record<string, unknown>;
    for (const key of ["error", "message", "detail"]) {
      const v = rec[key];
      if (typeof v === "string" && v.length > 0) return v;
    }
  }
  return fallback;
}

async function request<T>(method: "GET" | "POST", path: string, payload?: unknown): Promise<T> {
  if (!API_URL) {
    throw new Error("VITE_CRM_API_URL is not configured (see .env.example)");
  }
  const res = await fetch(API_URL + path, {
    method,
    headers: await authHeaders(),
    body: payload === undefined ? undefined : JSON.stringify(payload),
  });
  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      // non-JSON body; keep null
    }
  }
  if (!res.ok) {
    throw new Error(extractError(json, `${res.status} ${res.statusText}`));
  }
  return json as T;
}

function post<T>(path: string, payload?: unknown): Promise<T> {
  return request<T>("POST", path, payload);
}

function get<T>(path: string): Promise<T> {
  return request<T>("GET", path);
}

/** Send one message to a client. Worker rejects opted-out / phone-less clients with a clear error. */
export function sendSms(clientId: Id, body: string): Promise<Message> {
  return post<Message>("/sms/send", { client_id: clientId, body });
}

/** Check whether a phone number can receive iMessage (proxied to Project Blue). */
export function checkIMessage(phone: string): Promise<IMessageCheck> {
  return get<IMessageCheck>(`/sms/imessage?phone=${encodeURIComponent(phone)}`);
}

/**
 * Bulk text: recipients from a saved segment, an ad-hoc definition, or explicit
 * client ids. Template supports {first_name}.
 *
 * The worker caps each POST /sms/bulk call at 40 sends (Project Blue rate-limit
 * safety) and returns `remaining_client_ids`; this client keeps looping —
 * re-posting the remaining ids — until nothing remains, aggregating
 * {sent, skipped} across chunks and reporting progress via `onProgress`.
 *
 * If a chunk fails after earlier chunks already went out, a
 * BulkSendInterruptedError is thrown whose message says how many were sent.
 */
export async function sendBulk(args: BulkSmsArgs, onProgress?: BulkProgress): Promise<BulkSmsResult> {
  let sent = 0;
  const skipped: BulkSmsSkipped[] = [];
  let payload: BulkSmsArgs = args;
  // Hard stop well above any realistic client count (684 clients / 40 per chunk).
  const MAX_CHUNKS = 200;

  for (let chunk = 0; chunk < MAX_CHUNKS; chunk++) {
    let res: BulkChunkResponse;
    try {
      res = await post<BulkChunkResponse>("/sms/bulk", payload);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (sent > 0) throw new BulkSendInterruptedError({ sent, skipped }, msg);
      throw err;
    }
    sent += res.sent ?? 0;
    skipped.push(...(res.skipped ?? []));
    const remaining = res.remaining_client_ids ?? [];
    // Total is stable from the first chunk: everything sent or skipped so far
    // plus everything the worker says is still queued up.
    onProgress?.(sent, sent + skipped.length + remaining.length);
    if (remaining.length === 0) return { sent, skipped };
    if (res.rate_limited) {
      // Project Blue 60/min limit hit: an immediate re-post would spin with
      // zero progress. Wait out the window (capped), then continue the loop.
      const waitS = Math.min(Math.max(res.retry_after_seconds ?? 60, 5), 120);
      await new Promise((resolve) => setTimeout(resolve, waitS * 1000));
    }
    payload = { client_ids: remaining, body: args.body };
  }

  // MAX_CHUNKS exhausted with recipients still remaining — surface it rather
  // than silently dropping the tail.
  throw new BulkSendInterruptedError(
    { sent, skipped },
    `stopped after ${MAX_CHUNKS} chunks with recipients still unsent`,
  );
}

/** Manually trigger the campaign scheduler run (POST /campaigns/run). */
export function runCampaignsNow(): Promise<CampaignRunResult> {
  return post<CampaignRunResult>("/campaigns/run");
}

/**
 * Response of POST /ai/run — mirrors AiRunSummary in worker/src/ai.ts exactly.
 * When ANTHROPIC_API_KEY is unset the worker returns
 * {reminders_created: 0, recommendations: 0, skipped: "ANTHROPIC_API_KEY not set"}
 * rather than an error; `skipped` is always a human-readable reason string.
 * `errors` carries per-job failure messages when one of the two jobs threw.
 */
export interface AiRunResult {
  reminders_created: number;
  recommendations: number;
  /** Packages the note extractor filed via add_manual_package (older workers omit it). */
  packages_created?: number;
  /** Package mentions flagged for human review instead of auto-filed (count). */
  package_flags?: number;
  skipped?: string;
  errors?: string[];
}

/** Manually trigger the AI recommendation run (POST /ai/run). */
export function runAiNow(): Promise<AiRunResult> {
  return post<AiRunResult>("/ai/run");
}

/**
 * One attribution the AI chat filed (POST /ai/attribute). `amount` is the
 * sale's sale_total joined at read time by the worker — display-only here,
 * never stored by the attribution. Numbers may arrive serialized as strings.
 */
export interface AttributionFiled {
  sale_id: Id;
  client_name: string;
  channel: string;
  amount: number | string | null;
  referred_by_name: string | null;
}

/** Response of POST /ai/attribute — mirrors the worker's attribute endpoint. */
export interface AttributeChatResult {
  reply: string;
  filed: AttributionFiled[];
  needs_clarification: boolean;
}

/**
 * Send one owner chat message ("britney came from sms this time") to the
 * attribution AI (POST /ai/attribute). The worker parses it, files
 * sale_attributions rows, and returns a conversational reply plus the filed
 * rows.
 */
export function attributeChat(text: string): Promise<AttributeChatResult> {
  return post<AttributeChatResult>("/ai/attribute", { text });
}

/**
 * One outreach draft variant from POST /ai/draft: the message text plus the
 * dollar credit it promises (null = no offer — only red-color clients get
 * one). offer_amount is the draft's INTENT only; the credit actually filed at
 * send time is always parsed from the final text (lib/offers.ts).
 */
export interface AiDraft {
  text: string;
  offer_amount: number | null;
}

/** Full response of POST /ai/draft: up to three variant drafts for one client. */
export interface AiDraftSet {
  client_id: Id;
  variants: AiDraft[];
}

/**
 * Draft personalized outreach variants for one client (POST /ai/draft).
 *
 * Current workers return {variants: AiDraft[], client_id}; older ones return
 * a single {text, offer_amount, client_id}. Both shapes normalize to an
 * AiDraftSet — a lone draft becomes a one-element variants array — and a
 * response with no usable draft at all throws like any other draft failure.
 */
export async function draftFor(clientId: Id): Promise<AiDraftSet> {
  const raw = await post<unknown>("/ai/draft", { client_id: clientId });
  const rec = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;

  const variants: AiDraft[] = [];
  const push = (v: unknown) => {
    if (v == null || typeof v !== "object") return;
    const d = v as Record<string, unknown>;
    if (typeof d.text !== "string" || d.text.length === 0) return;
    const amt = typeof d.offer_amount === "string" ? Number(d.offer_amount) : d.offer_amount;
    variants.push({
      text: d.text,
      offer_amount: typeof amt === "number" && Number.isFinite(amt) ? amt : null,
    });
  };

  if (Array.isArray(rec.variants)) {
    for (const v of rec.variants) push(v);
  } else {
    push(rec); // pre-variants worker: the response IS the single draft
  }
  if (variants.length === 0) {
    throw new Error("the draft endpoint returned no usable drafts");
  }
  return { client_id: (rec.client_id as Id | undefined) ?? clientId, variants };
}

/** True when the worker responds OK on GET /health. Never throws. */
export async function apiHealth(): Promise<boolean> {
  if (!API_URL) return false;
  try {
    const res = await fetch(`${API_URL}/health`);
    return res.ok;
  } catch {
    return false;
  }
}
